import { useState, useEffect } from 'react';
import { httpPOST } from '@/utils/api';

interface StoredJSON {
  id: string;
  data: any;
  expiresAt: string;
  url: string;
}

export default function JSONStorageInterface() {
  const [jsonInput, setJsonInput] = useState('');
  const [isValidJSON, setIsValidJSON] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [storedJSONs, setStoredJSONs] = useState<StoredJSON[]>([]);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  // Load stored JSONs from localStorage on component mount
  useEffect(() => {
    const saved = localStorage.getItem('storedJSONs');
    if (saved) {
      try {
        const loadedJSONs = JSON.parse(saved);
        setStoredJSONs(loadedJSONs);
        // Expand only the newest entry (first in the array)
        if (loadedJSONs.length > 0) {
          setExpandedIds(new Set([loadedJSONs[0].id]));
        }
      } catch (error) {
        console.error('Error loading stored JSONs:', error);
      }
    }
  }, []);

  // Save stored JSONs to localStorage whenever it changes (excluding expired ones)
  useEffect(() => {
    const activeJSONs = storedJSONs.filter((item: StoredJSON) => !isExpired(item.expiresAt));
    localStorage.setItem('storedJSONs', JSON.stringify(activeJSONs));
  }, [storedJSONs]);

  // Validate JSON input
  useEffect(() => {
    if (!jsonInput.trim()) {
      setIsValidJSON(true);
      return;
    }
    
    try {
      JSON.parse(jsonInput);
      setIsValidJSON(true);
    } catch (error) {
      setIsValidJSON(false);
    }
  }, [jsonInput]);

  const handleStoreJSON = async () => {
    if (!isValidJSON || !jsonInput.trim()) return;

    setIsLoading(true);
    try {
      const response = await httpPOST('/quick', {
        jsonData: jsonInput,
        expires: '168' // 7 days (168 hours)
      });

      const newStoredJSON: StoredJSON = {
        id: response.id,
        data: response.data,
        expiresAt: response.expiresAt,
        url: `${window.location.origin}/api/quick/${response.id}`
      };

      setStoredJSONs((prev: StoredJSON[]) => [newStoredJSON, ...prev]);
      // Expand the newest entry and collapse all others
      setExpandedIds(new Set([newStoredJSON.id]));
      setJsonInput('');
    } catch (error) {
      console.error('Error storing JSON:', error);
      alert('Failed to store JSON. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = async (url: string, id: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
    }
  };

  const formatExpirationDate = (expiresAt: string) => {
    const date = new Date(expiresAt);
    const now = new Date();
    const diffTime = date.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays <= 0) {
      return 'Expired';
    } else if (diffDays === 1) {
      return 'Expires tomorrow';
    } else {
      return `Expires in ${diffDays} days`;
    }
  };

  const isExpired = (expiresAt: string) => {
    return new Date() > new Date(expiresAt);
  };

  const toggleExpanded = (id: string) => {
    setExpandedIds((prev: Set<string>) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const isExpanded = (id: string) => expandedIds.has(id);

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
        <h2 className="text-3xl font-bold text-white mb-6 text-center">
          Quick JSON Storage
        </h2>
        <p className="text-gray-300 text-center mb-4">
          Store your JSON data and get a shareable link. Data expires in 7 days.
        </p>

        {/* JSON Input Section */}
        <div className="mb-8">
          <label className="block text-sm font-medium text-gray-300 mb-2">
            JSON Data
          </label>
          <textarea
            value={jsonInput}
            onChange={(e) => setJsonInput(e.target.value)}
            placeholder='{"example": "data", "number": 123, "array": [1, 2, 3]}'
            className={`w-full h-32 px-4 py-3 rounded-lg bg-white/5 border ${
              isValidJSON ? 'border-gray-600' : 'border-red-500'
            } text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none`}
          />
          {!isValidJSON && (
            <p className="text-red-400 text-sm mt-1">Invalid JSON format</p>
          )}
          <button
            onClick={handleStoreJSON}
            disabled={!isValidJSON || !jsonInput.trim() || isLoading}
            className="mt-4 w-full bg-purple text-white px-6 py-3 rounded-lg font-semibold hover:bg-purple/80 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
          >
            {isLoading ? 'Storing...' : 'Store JSON & Get Link'}
          </button>
        </div>

        {/* Stored JSONs History */}
        {storedJSONs.length > 0 && (
          <div>
            <h3 className="text-xl font-semibold text-white mb-4">Your Stored JSONs</h3>
            <div className="space-y-4">
              {storedJSONs
                .filter(item => !isExpired(item.expiresAt))
                .map((item) => (
                <div
                  key={item.id}
                  className="p-4 rounded-lg border bg-white/5 border-white/10"
                >
                  <div className="flex items-center justify-between mb-2">
                    <button
                      onClick={() => toggleExpanded(item.id)}
                      className="text-gray-400 hover:text-white transition-colors"
                    >
                      {isExpanded(item.id) ? '▼' : '▶'}
                    </button>
                    <span className="text-sm text-gray-400">
                      {formatExpirationDate(item.expiresAt)}
                    </span>
                  </div>
                  
                  {isExpanded(item.id) && (
                    <>
                      <div className="mb-3">
                        <pre className="text-xs text-gray-300 bg-black/20 p-2 rounded overflow-x-auto">
                          {JSON.stringify(item.data, null, 2)}
                        </pre>
                      </div>

                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={item.url}
                          readOnly
                          className="flex-1 px-3 py-2 bg-black/20 border border-gray-600 rounded text-sm text-gray-300 font-mono"
                        />
                        <button
                          onClick={() => copyToClipboard(item.url, item.id)}
                          className="px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded text-sm transition-colors"
                        >
                          {copiedId === item.id ? 'Copied!' : 'Copy'}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}